import React from "react";
import styled, { css } from "styled-components";

import useMyDrawings from "../../hooks/useMyDrawings";
import GalleryCard from "../GalleryCard";

const MARC_HASH_RE = /^#?(\d{3})$/;

function readMarcFilter() {
  const m = MARC_HASH_RE.exec(window.location.hash);
  return m ? m[1] : null;
}

function readPermalinkId() {
  return new URLSearchParams(window.location.search).get("id");
}

function clearMarcFilter(e) {
  e.preventDefault();
  // history.pushState + manual hashchange dispatch — assigning '' to
  // location.hash leaves a stray '#' in the URL bar.
  window.history.pushState(null, "", window.location.pathname);
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

function setMarcFilterUrl(e, marcId) {
  e.preventDefault();
  e.stopPropagation();
  window.history.pushState(null, "", `${window.location.pathname}#${marcId}`);
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

function formatDate(ms) {
  return new window.Date(ms).toLocaleDateString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
}

function Gallery() {
  const [tab, setTab] = React.useState("public");
  const [mineFilter, setMineFilter] = React.useState("all");

  // Public listing: one fetch on mount. Marc-hash and ?id= permalink
  // are applied client-side over the cached results — at current
  // scale the round-trip on every hash change wasn't worth it. When
  // the gallery grows enough to need windowing, the API still
  // supports server-side filters; drop them back in then.
  const [allPublic, setAllPublic] = React.useState([]);
  const [publicLoading, setPublicLoading] = React.useState(true);
  const [publicError, setPublicError] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/gallery?limit=50")
      .then((r) => {
        if (!r.ok) throw new Error(`gallery fetch failed (${r.status})`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setAllPublic(data.items || []);
        setPublicLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setPublicError(e.message);
        setPublicLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [marcFilter, setMarcFilter] = React.useState(() => readMarcFilter());
  const [permalinkId, setPermalinkId] = React.useState(() => readPermalinkId());
  React.useEffect(() => {
    const sync = () => {
      setMarcFilter(readMarcFilter());
      setPermalinkId(readPermalinkId());
    };
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener("hashchange", sync);
      window.removeEventListener("popstate", sync);
    };
  }, []);

  const publicItems = React.useMemo(() => {
    if (permalinkId) return allPublic.filter((i) => i.id === permalinkId);
    if (marcFilter) return allPublic.filter((i) => i.marcId === marcFilter);
    return allPublic;
  }, [allPublic, permalinkId, marcFilter]);

  const { items: mineItems, loading: mineLoading } = useMyDrawings(mineFilter);

  const isPublic = tab === "public";
  const items = isPublic ? publicItems : mineItems;
  const loading = isPublic ? publicLoading : mineLoading;
  const error = isPublic ? publicError : null;

  let banner = null;
  if (isPublic && permalinkId) {
    banner = (
      <FilterChip>
        Showing one egg
        <ChipDot />
        <a href="/gallery">Back to gallery</a>
      </FilterChip>
    );
  } else if (isPublic && marcFilter) {
    banner = (
      <FilterChip>
        Filtering to Marc {marcFilter}
        <ChipDot />
        <a href="#" onClick={clearMarcFilter}>
          Show all
        </a>
      </FilterChip>
    );
  }

  let emptyMsg = null;
  if (!loading && !error && items.length === 0) {
    if (isPublic) emptyMsg = "no eggs yet";
    else if (mineFilter === "syndicated")
      emptyMsg = "You haven't syndicated yet. Be brave.";
    else emptyMsg = "You haven't shipped a Marc. The world is waiting.";
  }

  return (
    <Page>
      <Header>
        <TitleLink href="/">Our Marcs</TitleLink>
        <Subtitle>
          We Believe in Drawing Marc on Eggs, and We Built the Site that Way.
        </Subtitle>
      </Header>

      <TabSection>
        <Tabs>
          <Tab $active={isPublic} onClick={() => setTab("public")}>
            Public Marcs
          </Tab>
          <Tab $active={!isPublic} onClick={() => setTab("mine")}>
            Private Marcs
          </Tab>
        </Tabs>
        {/* Always rendered so switching tabs doesn't shift the grid;
            invisible + inert when there's nothing to show. */}
        <Filters aria-hidden={isPublic} $hidden={isPublic}>
          <Chip
            $active={mineFilter === "all"}
            onClick={() => setMineFilter("all")}
          >
            All
          </Chip>
          <Chip
            $active={mineFilter === "syndicated"}
            onClick={() => setMineFilter("syndicated")}
          >
            Syndicated
          </Chip>
        </Filters>
      </TabSection>
      {banner}
      {loading && <Status>loading…</Status>}
      {error && <Status>error: {error}</Status>}
      {emptyMsg && <Status>{emptyMsg}</Status>}

      <GalleryPanel>
        <Grid>
          {items.map((item) => (
            <GalleryCard
              key={item.id}
              item={item}
              href={isPublic ? `/gallery?id=${item.id}` : undefined}
              meta={
                isPublic ? <PublicMeta item={item} /> : <MineMeta item={item} />
              }
              footer={
                isPublic ? (
                  <MetaButton onClick={(e) => setMarcFilterUrl(e, item.marcId)}>
                    Comparable marcs →
                  </MetaButton>
                ) : null
              }
            />
          ))}
        </Grid>
      </GalleryPanel>
    </Page>
  );
}

function PublicMeta({ item }) {
  const mode = item.source === "speedrun" ? `Speedrun` : "Creative";
  return (
    <MetaList>
      <dt>Builder</dt>
      <dd>{item.initials}</dd>
      <dt>Subject</dt>
      <dd>Marc Andreessen</dd>
      <dt>Mode</dt>
      <dd>{mode}</dd>
      <dt>Built</dt>
      <dd>{formatDate(item.createdAt)}</dd>
    </MetaList>
  );
}

function MineMeta({ item }) {
  const mode = item.source === "speedrun" ? `Speedrun` : "Creative";
  const status = item.submission
    ? `Syndicated · ${item.submission.initials}`
    : "Local only";
  return (
    <MetaList>
      <dt>Subject</dt>
      <dd>Marc Andreessen</dd>
      <dt>Mode</dt>
      <dd>{mode}</dd>
      <dt>Built</dt>
      <dd>{formatDate(item.createdAt)}</dd>
      <dt>Status</dt>
      <dd>{status}</dd>
    </MetaList>
  );
}

const Page = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2.5rem clamp(0.5rem, 3vw, 1.25rem) 4rem;
  font-family: "Nunito", system-ui, sans-serif;
`;

const Header = styled.header`
  margin-bottom: 1.5rem;
  text-align: center;
`;

const TitleLink = styled.a`
  display: inline-block;
  font-family: "Fraunces", serif;
  font-size: clamp(2.5rem, 6vw, 5rem);
  line-height: 1;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-text);
  text-decoration: none;
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.5),
    0 2px 6px rgba(80, 45, 20, 0.18);

  &:hover {
    text-decoration: underline;
    text-decoration-thickness: 4px;
  }
`;

const Subtitle = styled.p`
  margin-top: 0.5rem;
  font-style: italic;
  color: var(--color-text-muted);
  font-size: clamp(0.95rem, 1.2vw, 1.125rem);
  max-width: 38rem;
  margin-left: auto;
  margin-right: auto;
  text-wrap: balance;
`;

const TabSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const Tabs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem 1rem;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--color-orange-300);
`;

const Tab = styled.button`
  padding: 0.5rem 0.25rem;
  background: none;
  border: none;
  cursor: pointer;
  font-family: "Fraunces", serif;
  font-size: 1.125rem;
  color: var(--color-text-muted);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  white-space: nowrap;

  ${(p) =>
    p.$active &&
    css`
      color: var(--color-text);
      border-bottom-color: var(--color-orange-700);
    `};
`;

const Filters = styled.div`
  display: flex;
  gap: 0.375rem;

  ${(p) =>
    p.$hidden &&
    css`
      visibility: hidden;
      pointer-events: none;
    `}
`;

const Chip = styled.button`
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-radius: 999px;
  border: 1px solid var(--color-orange-300);
  background: ${(p) =>
    p.$active ? "var(--color-orange-700)" : "var(--color-orange-50, #fff8f1)"};
  color: ${(p) => (p.$active ? "white" : "var(--color-text-muted)")};
  cursor: pointer;
`;

const FilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  margin-bottom: 1rem;
  border-radius: 999px;
  background: var(--color-orange-200);
  border: 1px solid var(--color-orange-300);
  font-size: 0.875rem;
  color: var(--color-text);

  a {
    color: var(--color-orange-700);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const ChipDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--color-text-muted);
`;

const Status = styled.div`
  padding: 2rem 0;
  color: var(--color-text-muted);
`;

const GalleryPanel = styled.div`
  /* Cream wash that softens the graph paper underneath, with a radial
     mask so the edges fade into the page. backdrop-filter blurs the
     grid lines showing through. */
  position: relative;
  padding: 1rem clamp(0.5rem, 3vw, 1.25rem) 1.25rem;
  border-radius: 24px;
  background: rgba(255, 247, 237, 0.1);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  -webkit-mask-image: radial-gradient(
    ellipse 100% 100% at center,
    black 80%,
    transparent 100%
  );
  mask-image: radial-gradient(
    ellipse 100% 100% at center,
    black 80%,
    transparent 100%
  );
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 420px));
  gap: 1.5rem;
  justify-content: center;
`;

const MetaList = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 0.625rem;
  row-gap: 0.3125rem;
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.35;

  dt {
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.6875rem;
    align-self: center;
  }

  dd {
    margin: 0;
    color: var(--color-text);
    font-family: "Fraunces", serif;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MetaButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.8125rem;
  color: var(--color-orange-700);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: var(--color-orange-800);
  }
`;

export default Gallery;
